import React, { useMemo } from 'react';
import { Button, Card, Col, Descriptions, Row, Space, Tag, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import type {
  RouteViewModelOperation,
  RouteViewModelParties,
  RouteViewModelStructure,
} from '../../../types/routeViewModel';
import { StatusCell } from '../../../ui';
import { TransportType } from '../../../types';
import { executorTypeLabel } from '../../../utils/enumUiLabels';
import { navigateToReferenceCard } from '../../../utils/referenceCardNavigation';
import { isSequentialStopsReadModeEnabled } from '../../../features/sequentialStops/sequentialStopsReadFlags';
import { sortStopsByPosition } from '../../../features/sequentialStops/sequentialStopsOrdering';
import { buildRouteDirectionLabel } from '../../../features/routes/routeSummaryProjection';
import {
  getStopExecutionActualAt,
} from '../../../features/routes/routeViewExecutionHelpers';
import {
  RouteApplicationsCompactText,
  RouteClientsCompactText
} from '../../../features/routes/RouteMultiAppCompactCells';
import { resolvePrimaryApplicationFromRows, formatRouteClientsCompact, type RouteApplicationJoinRow } from '../../../features/routes/routeMultiApplicationDisplay';
import { formatResponsibleUserLabel } from '../../../utils/userDisplayName';

const { Text } = Typography;

const sectionCardStyle: React.CSSProperties = { height: '100%' };
const sectionBodyStyle = { body: { padding: '12px 14px' } };

const transportLabels: Record<string, string> = {
  [TransportType.AUTO]: 'Автомобильный',
  [TransportType.RAILWAY]: 'Железнодорожный',
  [TransportType.AVIATION]: 'Авиационный',
};

function entityId(entity: unknown): number | null {
  if (!entity || typeof entity !== 'object') return null;
  const id = (entity as { id?: unknown }).id;
  return typeof id === 'number' && Number.isFinite(id) ? id : null;
}

function partyName(entity: unknown, field: 'name' | 'fullName' = 'name'): string | null {
  if (!entity || typeof entity !== 'object') return null;
  const raw = (entity as Record<string, unknown>)[field];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function vehicleLabel(vehicle: Record<string, unknown> | null | undefined): string {
  if (!vehicle) return '—';
  const brand = vehicle.brand != null ? String(vehicle.brand).trim() : '';
  const name = vehicle.name != null ? String(vehicle.name).trim() : '';
  const registrationNumber =
    vehicle.registrationNumber != null ? String(vehicle.registrationNumber).trim() : '';
  const parts = [name || brand, registrationNumber].filter(Boolean);
  return parts.join(' · ') || '—';
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD.MM.YYYY') : '—';
}

function extractRoutePoints(structure: RouteViewModelStructure): Record<string, unknown>[] {
  const rawPoints = (structure as RouteViewModelStructure & { points?: Record<string, unknown>[] }).points;
  if (Array.isArray(rawPoints)) return rawPoints;
  return Array.isArray(structure.stops) ? (structure.stops as Record<string, unknown>[]) : [];
}

function formatCarrierContract(
  isCompanyRoute: boolean,
  carrierExists: boolean,
  contract: { number: string | null; date: string | null }
): React.ReactNode {
  if (isCompanyRoute) {
    return <Tag color="green">Свой</Tag>;
  }
  if (!carrierExists) return '—';

  const number = contract.number?.trim();
  const date = formatDate(contract.date);
  if (number && date !== '—') return `№${number} от ${date}`;
  if (number) return `№${number}`;
  if (date !== '—') return `от ${date}`;
  return '—';
}

type EntityLinkProps = {
  id: number | null;
  path: string;
  label: string;
};

const EntityLink: React.FC<EntityLinkProps> = ({ id, path, label }) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (id == null || label === '—') {
    return <>{label}</>;
  }

  return (
    <Button
      type="link"
      size="small"
      className="routes-entity-link"
      style={{ height: 'auto', padding: 0 }}
      onClick={(e) => {
        e.stopPropagation();
        navigateToReferenceCard(navigate, path, location);
      }}
    >
      {label}
    </Button>
  );
};

const InfoSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Card size="small" title={title} style={sectionCardStyle} styles={sectionBodyStyle}>
    <Descriptions column={1} size="small">
      {children}
    </Descriptions>
  </Card>
);

export interface RoutePrimaryInfoBlockProps {
  model: RouteViewModelOperation;
  structure: RouteViewModelStructure;
  parties: RouteViewModelParties;
  applications: Record<string, unknown>[];
  routeNumber?: string | null;
}

const RoutePrimaryInfoBlock: React.FC<RoutePrimaryInfoBlockProps> = ({
  model,
  structure,
  parties,
  applications,
  routeNumber,
}) => {
  const applicationRows = applications as RouteApplicationJoinRow[];
  const primaryApplication = resolvePrimaryApplicationFromRows(applicationRows) as Record<string, unknown> | undefined;

  const clientSummary = useMemo(
    () => formatRouteClientsCompact(applicationRows, parties.client as { id?: number; name?: string | null } | null),
    [applicationRows, parties.client]
  );

  const clientId = useMemo(() => {
    return clientSummary.primaryClientId ?? entityId(parties.client) ?? entityId(primaryApplication?.client);
  }, [clientSummary.primaryClientId, parties.client, primaryApplication]);

  const clientName = clientSummary.count > 0 ? clientSummary.display : '—';

  const carrierId = entityId(parties.carrier);
  const carrierLabel = partyName(parties.carrier) ?? (model.isCompanyRoute ? 'Свой' : '—');
  const shipperLabel = partyName(parties.shipper) ?? partyName(primaryApplication?.shipper) ?? '—';
  const consigneeLabel = partyName(parties.consignee) ?? partyName(primaryApplication?.consignee) ?? '—';
  const driverId = entityId(parties.driver);
  const driverLabel = partyName(parties.driver, 'fullName') ?? '—';
  const vehicleId = entityId(parties.vehicle);
  const trailerVehicleId = entityId(parties.trailerVehicle ?? null);
  const vehicleText = vehicleLabel(parties.vehicle);
  const trailerText = vehicleLabel(parties.trailerVehicle ?? null);
  const routeTypeTransport = `${executorTypeLabel(model.executor)} / ${transportLabels[model.transportType] ?? '—'}`;
  const routeNumberLabel = routeNumber?.trim() ? routeNumber.trim() : `#${model.id}`;
  const points = useMemo(() => extractRoutePoints(structure), [structure]);
  const orderedPoints = useMemo(() => sortStopsByPosition(points), [points]);
  const sequentialRead = isSequentialStopsReadModeEnabled();
  const loadingPoint = useMemo(() => {
    if (sequentialRead && orderedPoints.length > 0) {
      const first = orderedPoints[0];
      return String(first.type || '').toLowerCase() === 'loading' ? first : null;
    }
    const items = points
      .filter((point) => String(point.type || '').toLowerCase() === 'loading')
      .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
    return items[0] ?? null;
  }, [points, orderedPoints, sequentialRead]);
  const unloadingPoint = useMemo(() => {
    if (sequentialRead && orderedPoints.length > 0) {
      const last = orderedPoints[orderedPoints.length - 1];
      return String(last.type || '').toLowerCase() === 'unloading' ? last : null;
    }
    const items = points
      .filter((point) => String(point.type || '').toLowerCase() === 'unloading')
      .sort((a, b) => Number(a.position ?? 0) - Number(b.position ?? 0));
    return items.length > 0 ? items[items.length - 1] : null;
  }, [points, orderedPoints, sequentialRead]);
  const plannedLoadingDate = formatDate((loadingPoint?.plannedAt as string | null | undefined) ?? model.dates.plannedLoading);
  const actualLoadingDate = formatDate(
    (loadingPoint ? getStopExecutionActualAt(loadingPoint) : null) ?? model.dates.actualLoading
  );
  const plannedUnloadingDate = formatDate((unloadingPoint?.plannedAt as string | null | undefined) ?? model.dates.plannedUnloading);
  const actualUnloadingDate = formatDate(
    (unloadingPoint ? getStopExecutionActualAt(unloadingPoint) : null) ?? model.dates.actualUnloading
  );
  const directionFromStops = buildRouteDirectionLabel(orderedPoints, {
    routeLoadingCity: model.direction.loadingCity,
    routeUnloadingCity: model.direction.unloadingCity,
    loadingWarehouse: model.loadingWarehouse as { id?: number; city?: string | null } | null,
    unloadingWarehouse: model.unloadingWarehouse as { id?: number; city?: string | null } | null
  });
  const displayDirectionLabel =
    directionFromStops !== 'Не указано'
      ? directionFromStops
      : model.direction.label?.trim() || '—';

  return (
    <Card size="small" title="Основная информация" style={{ marginBottom: 16 }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Row gutter={[12, 12]}>
          <Col xs={24} lg={12}>
            <InfoSection title="Общая информация">
              <Descriptions.Item label="№ маршрута">
                <Text strong>{routeNumberLabel}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Статус">
                <StatusCell status={String(model.status || '')} />
              </Descriptions.Item>
              <Descriptions.Item label="Тип маршрута / транспорта">{routeTypeTransport}</Descriptions.Item>
              <Descriptions.Item label="Направление">
                <Text strong>{displayDirectionLabel}</Text>
              </Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Даты">
              <Descriptions.Item label="Плановая погрузка">{plannedLoadingDate}</Descriptions.Item>
              <Descriptions.Item label="Плановая выгрузка">{plannedUnloadingDate}</Descriptions.Item>
              <Descriptions.Item label="Фактическая погрузка">{actualLoadingDate}</Descriptions.Item>
              <Descriptions.Item label="Фактическая выгрузка">{actualUnloadingDate}</Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Участники">
              <Descriptions.Item label="Заявки">
                <RouteApplicationsCompactText applications={applicationRows} />
              </Descriptions.Item>
              <Descriptions.Item label="Клиент">
                <RouteClientsCompactText
                  applications={applicationRows}
                  fallbackClient={parties.client as { id?: number | null; name?: string | null } | null}
                  renderPrimaryLink={
                    clientId != null && clientName !== '—'
                      ? ({ label }) => (
                          <EntityLink id={clientId} path={`/clients/${clientId}`} label={label} />
                        )
                      : undefined
                  }
                  emptyLabel="—"
                />
              </Descriptions.Item>
              <Descriptions.Item label="Грузоотправитель">{shipperLabel}</Descriptions.Item>
              <Descriptions.Item label="Грузополучатель">{consigneeLabel}</Descriptions.Item>
              <Descriptions.Item label="Перевозчик">
                {model.isCompanyRoute ? (
                  <Tag color="green">Свой</Tag>
                ) : (
                  <EntityLink id={carrierId} path={`/carriers/${carrierId}`} label={carrierLabel} />
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Логист">
                {formatResponsibleUserLabel(model.responsibleLogist)}
              </Descriptions.Item>
            </InfoSection>
          </Col>
          <Col xs={24} lg={12}>
            <InfoSection title="Исполнение">
              <Descriptions.Item label="Водитель">
                <EntityLink id={driverId} path={`/drivers/${driverId}`} label={driverLabel} />
              </Descriptions.Item>
              <Descriptions.Item label="ТС">
                <EntityLink id={vehicleId} path={`/vehicles/${vehicleId}`} label={vehicleText} />
              </Descriptions.Item>
              <Descriptions.Item label="Прицеп">
                <EntityLink id={trailerVehicleId} path={`/vehicles/${trailerVehicleId}`} label={trailerText} />
              </Descriptions.Item>
            </InfoSection>
          </Col>
        </Row>
        <InfoSection title="Договор">
          <Descriptions.Item label="Договор-заявка">
            {formatCarrierContract(model.isCompanyRoute, carrierId != null, model.carrierContractRequest)}
          </Descriptions.Item>
        </InfoSection>
      </Space>
    </Card>
  );
};

export default RoutePrimaryInfoBlock;
